import { Backdrop, Modal } from '@mui/material';
import { Box } from '@material-ui/core';
import React, { useMemo, useState, useEffect } from 'react';
import i18n from '../../i18n/i18n';
import CountryPicker from '../CountryPicker';
import { countryData } from '../../constants/CountryData';

type TaxCountryModalProps = {
    isSelectingCountry: boolean;
    setSelectingCountry: (selecting: boolean) => void;
    country: string;
    handleCountrySelected: (countryCode: string) => void;
    variant?: string;
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80dvw',
  minHeight: '50dvh',
  bgcolor: 'background.paper',
  border: '2px solid #f0b239',
  borderRadius: '10px',
  boxShadow: 24,
  p: 4,
};

const galpBoxStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '88dvw',
  maxWidth: '360px',
  maxHeight: '82dvh',
  bgcolor: 'white',
  borderRadius: '14px',
  boxShadow: 24,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  outline: 'none',
};

const TaxCountryModal = ({
  isSelectingCountry, setSelectingCountry, handleCountrySelected, country, variant,
}: TaxCountryModalProps) => {
  const countryOptions = useMemo(() => Object
    .keys(countryData)
    .map((c) => ({
      value: c,
      label: countryData[c]?.component,
    })), []);

  // Galp two-step flow (design 518-2630): pick a row, then Confirm.
  const [tempCountry, setTempCountry] = useState(country);
  useEffect(() => {
    if (isSelectingCountry) setTempCountry(country);
  }, [isSelectingCountry, country]);

  // Localised country names straight from the ISO code — no per-country i18n keys.
  const displayNames = useMemo(() => {
    try {
      const localeTag = (i18n.language || 'ptPT').replace(/([a-z])([A-Z])/, '$1-$2');
      return new Intl.DisplayNames([localeTag], { type: 'region' });
    } catch (e) {
      return null;
    }
  }, [i18n.language]);
  const countryName = (code: string): string => {
    try {
      return (displayNames && displayNames.of(code)) || code;
    } catch (e) {
      return code;
    }
  };

  if (variant === 'galp') {
    const galpOptions = [...countryOptions].sort(
      (a, b) => countryName(a.value).localeCompare(countryName(b.value)),
    );

    return (
      <Modal
        open={isSelectingCountry}
        onClose={() => setSelectingCountry(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Box sx={galpBoxStyle}>
          <h2 style={{
            fontFamily: "'Ping LCG', Poppins, sans-serif",
            fontSize: '15px',
            fontWeight: 400,
            color: '#111110',
            textAlign: 'left',
            margin: 0,
            padding: '20px 20px 12px',
          }}
          >
            {i18n.t('select_country_short')}
          </h2>

          <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px' }}>
            {galpOptions.map((option) => {
              const CountryFlag = option.label;
              const selected = tempCountry === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setTempCountry(option.value)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px',
                    width: '100%',
                    padding: '12px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    fontFamily: "'Ping LCG', Poppins, sans-serif",
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#0b0b0b',
                    textAlign: 'left',
                  }}
                >
                  <span style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: `2px solid ${selected ? '#be2e00' : '#bab9ba'}`,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                  }}
                  >
                    {selected && (
                      <span style={{
                        width: '10px', height: '10px', borderRadius: '50%', background: '#be2e00',
                      }}
                      />
                    )}
                  </span>
                  <span style={{
                    width: '28px',
                    height: '19px',
                    borderRadius: '3px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    display: 'block',
                    lineHeight: 0,
                  }}
                  >
                    <CountryFlag style={{ width: '28px', height: '19px', display: 'block' }} />
                  </span>
                  <span>{countryName(option.value)}</span>
                </button>
              );
            })}
          </div>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            padding: '14px 20px 18px',
            borderTop: '1px solid #eee',
          }}
          >
            <button
              type="button"
              onClick={() => {
                handleCountrySelected(tempCountry);
                setSelectingCountry(false);
              }}
              style={{
                height: '40px',
                borderRadius: '57px',
                border: 'none',
                background: '#be2e00',
                color: 'white',
                fontFamily: "'Ping LCG', Poppins, sans-serif",
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              {i18n.t('confirm')}
            </button>
            <button
              type="button"
              onClick={() => setSelectingCountry(false)}
              style={{
                height: '40px',
                borderRadius: '57px',
                border: '1px solid #bab9ba',
                background: 'white',
                color: '#0b0b0b',
                fontFamily: "'Ping LCG', Poppins, sans-serif",
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              {i18n.t('cancel')}
            </button>
          </div>
        </Box>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={isSelectingCountry}
        onClose={() => setSelectingCountry(false)}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Box sx={style}>
          <CountryPicker
            country={country}
            options={countryOptions}
            onCountrySelected={handleCountrySelected}
          />
        </Box>
      </Modal>
    </>
  );
};

export default TaxCountryModal;
